/**
 * UndoBanner — fixed bottom-center banner with 60s countdown undo window.
 *
 * Props:
 *   action    — {decisionId, type: "platform"|"legal", label, timestamp}
 *   onUndo    — (decisionId) called after successful POST /undo, then banner closes
 *   onExpire  — called when the countdown hits zero (parent removes the banner)
 *
 * Legal decisions show "no undo" message and auto-close after 5s.
 * POST /api/admin/ops/decisions/:id/undo is called before invoking onUndo.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { CheckCircle, X, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";

// ── Constants ─────────────────────────────────────────────────────────────────

const TOTAL_SECONDS = 60;
const LEGAL_CLOSE_SECONDS = 5;

// ── Auth helper ───────────────────────────────────────────────────────────────

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── UndoBanner ────────────────────────────────────────────────────────────────

export default function UndoBanner({ action, onUndo, onExpire }) {
  const isLegal = action?.type === "legal";
  const total = isLegal ? LEGAL_CLOSE_SECONDS : TOTAL_SECONDS;

  const [secondsLeft, setSecondsLeft] = useState(total);
  const [undoing, setUndoing] = useState(false);
  // Animate-in: start invisible, flip to visible after first paint
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef(null);

  // Slide in on mount
  useEffect(() => {
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, []);

  // Countdown — resets whenever action changes
  useEffect(() => {
    if (!action) return;
    setSecondsLeft(total);

    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          if (onExpire) onExpire();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [action, total, onExpire]);

  const handleUndo = useCallback(async () => {
    if (undoing || !action?.decisionId) return;
    clearInterval(intervalRef.current);
    setUndoing(true);

    try {
      await axios.post(
        `${API}/admin/ops/decisions/${action.decisionId}/undo`,
        {},
        { headers: authHeaders() }
      );
      toast("Action undone");
      if (onUndo) onUndo(action.decisionId);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "Undo failed — action may already be committed";
      toast.error(msg);
      setUndoing(false);
      // Restart remaining countdown
      intervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            if (onExpire) onExpire();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [undoing, action, onUndo, onExpire]);

  function handleClose() {
    clearInterval(intervalRef.current);
    if (onExpire) onExpire();
  }

  if (!action) return null;

  const progress = secondsLeft / total; // 1.0 → 0.0

  return (
    <>
      <div
        className={[
          "mc-undo-banner",
          visible ? "mc-undo-banner--visible" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="status"
        aria-live="polite"
        aria-label={
          isLegal
            ? "Legal decision committed — no undo available"
            : `Decision action: ${action.label}. ${secondsLeft}s to undo.`
        }
      >
        {/* ── Main row ── */}
        <div className="mc-undo-banner-row">
          <CheckCircle
            size={14}
            className="mc-undo-check"
            aria-hidden="true"
          />

          <span className="mc-undo-label">
            {isLegal
              ? "Legal decision committed — no undo"
              : (
                <span className="mc-undo-verb">
                  {action.label || "Action taken"}
                </span>
              )}
          </span>

          <div className="mc-undo-right">
            {/* UNDO button — platform only */}
            {!isLegal && (
              <button
                className="mc-undo-btn"
                onClick={handleUndo}
                disabled={undoing || secondsLeft === 0}
                aria-label="Undo this decision"
              >
                {undoing ? (
                  <>
                    <Loader2 size={11} className="mc-undo-spin" aria-hidden="true" />
                    Undoing…
                  </>
                ) : (
                  "UNDO"
                )}
              </button>
            )}

            {/* Dismiss */}
            <button
              className="mc-undo-close"
              onClick={handleClose}
              aria-label="Dismiss"
            >
              <X size={13} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* ── Progress bar row ── */}
        <div className="mc-undo-progress-wrap" aria-hidden="true">
          <div
            className="mc-undo-progress-bar"
            style={{ width: `${progress * 100}%` }}
          />
          {!isLegal && (
            <span className="mc-undo-countdown mc-mono">
              {secondsLeft}s left
            </span>
          )}
        </div>
      </div>

      <style>{`
        .mc-undo-banner {
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%) translateY(16px);
          opacity: 0;
          width: 480px;
          max-width: calc(100vw - 32px);
          background-color: var(--mc-surface);
          border: 1px solid var(--color-green);
          border-radius: var(--radius-md, 6px);
          padding: 12px 14px 10px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          z-index: 200;
          box-sizing: border-box;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.28);
          transition:
            transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
            opacity 220ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .mc-undo-banner--visible {
          transform: translateX(-50%) translateY(0);
          opacity: 1;
        }

        /* Main row */
        .mc-undo-banner-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mc-undo-check {
          color: var(--color-green);
          flex-shrink: 0;
        }

        .mc-undo-label {
          flex: 1;
          font-size: 13px;
          font-family: var(--font-sans);
          color: var(--mc-text);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }

        .mc-undo-verb {
          font-weight: 500;
        }

        .mc-undo-right {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        /* UNDO button */
        .mc-undo-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: var(--radius-md, 6px);
          background: transparent;
          border: 1px solid var(--color-green);
          color: var(--color-green);
          font-size: 11px;
          font-weight: 700;
          font-family: var(--font-sans);
          letter-spacing: 0.08em;
          cursor: pointer;
          transition:
            background-color var(--dur, 150ms) var(--ease, ease);
        }

        .mc-undo-btn:hover:not(:disabled) {
          background-color: rgba(23, 128, 61, 0.12);
        }

        .mc-undo-btn:disabled {
          opacity: 0.40;
          cursor: not-allowed;
        }

        /* Dismiss button */
        .mc-undo-close {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: transparent;
          border: none;
          color: var(--color-slate-500);
          cursor: pointer;
          border-radius: 4px;
          transition: color var(--dur, 150ms) var(--ease, ease);
          padding: 0;
        }

        .mc-undo-close:hover {
          color: var(--mc-text);
        }

        /* Progress bar */
        .mc-undo-progress-wrap {
          position: relative;
          height: 3px;
          background-color: var(--mc-border);
          border-radius: 2px;
          overflow: visible;
        }

        .mc-undo-progress-bar {
          height: 100%;
          background-color: var(--color-green);
          border-radius: 2px;
          transition: width 1s linear;
        }

        .mc-undo-countdown {
          position: absolute;
          right: 0;
          top: 6px;
          font-size: 10px;
          color: var(--color-slate-500);
          white-space: nowrap;
        }

        @keyframes mc-spin {
          to { transform: rotate(360deg); }
        }

        .mc-undo-spin {
          animation: mc-spin 0.8s linear infinite;
        }

        @media (max-width: 520px) {
          .mc-undo-banner {
            width: calc(100vw - 32px);
          }
        }
      `}</style>
    </>
  );
}
